/**
 * web-studio-sites D15 — the services piece's times, cut from the studio's one
 * shared answer. The answer holds each listed service's start instants over
 * its first days with openings in the studio's own zone; here they are grouped
 * into the visitor's own days, as the per-service next-openings read groups
 * them. The answer can be up to two minutes old, so a start that has gone by
 * `now` is left out, as the live read leaves it out. Pure and browser-safe.
 */

export interface OpenStartsDay {
  /** The visitor's calendar day, YYYY-MM-DD in their zone. */
  readonly date: string;
  /** Start instants (UTC, ISO 8601), in time order. */
  readonly starts: readonly string[];
}

/** The first `days` days in `timezone` with a start after `now`, up to `perDay` starts each; each instant once. */
export function groupOpenStarts(
  starts: readonly string[],
  timezone: string,
  now: number,
  opts: { readonly days: number; readonly perDay: number },
): OpenStartsDay[] {
  const dayParts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' });
  const dayOf = (t: number) => {
    const part = (type: string) => dayParts.formatToParts(t).find((p) => p.type === type)!.value;
    return `${part('year')}-${part('month')}-${part('day')}`;
  };
  const instants = [...new Set(starts.map((s) => Date.parse(s)))].filter((t) => t > now).sort((a, b) => a - b);

  const out: { date: string; starts: string[] }[] = [];
  for (const t of instants) {
    const date = dayOf(t);
    let day = out[out.length - 1];
    if (day?.date !== date) {
      if (out.length === opts.days) break;
      day = { date, starts: [] };
      out.push(day);
    }
    if (day.starts.length < opts.perDay) day.starts.push(new Date(t).toISOString());
  }
  return out;
}
